import type { Action } from 'modules/global/generic.types';
import type { MayBe } from 'modules/global/typing/common/common';

import type { MatcherFunction } from './reducers.types';

/** The three phases of the `<ROOT>/REQUEST|SUCCESS|FAILURE` action convention. */
export type RequestPhase = 'REQUEST' | 'SUCCESS' | 'FAILURE';

/**
 * Resolves which phase of a given root an action belongs to. Returns `false` — not `undefined` —
 * when the root itself is not a `*REQUEST` type, because the whole matcher is then disabled.
 */
export type RequestPhaseResolver = (action: Action) => MayBe<RequestPhase> | false;

export type RequestMatcherConfig = {
  /** Extra predicate the action must also satisfy (e.g. `meta.source`). */
  matcher?: MatcherFunction;
  /** Replaces the phase resolution entirely; defaults to the `<ROOT>/PHASE` convention. */
  matchRequest?: (actionRoot: string) => RequestPhaseResolver;
};

/**
 * Builds the phase resolver for an action root.
 * @param actionRootType the `*REQUEST` action type the reducer is built around
 * @example
 *   isRequestMatcher('DEALERS_FETCH_REQUEST')({ type: 'DEALERS_FETCH_SUCCESS' }) // 'SUCCESS'
 *   isRequestMatcher('DEALERS_FETCH_REQUEST')({ type: LOCATION_CHANGE })         // undefined
 *   isRequestMatcher('DEALERS_FETCH')({ type: 'DEALERS_FETCH' })                 // false
 */
const isRequestMatcher = (actionRootType: string): RequestPhaseResolver => {
  const rootType = actionRootType.toString();
  const isRequest = rootType.endsWith('REQUEST');
  const simpleType = rootType.substring(0, rootType.length - 7);
  const regExp = new RegExp(`^${simpleType}(REQUEST|SUCCESS|FAILURE)$`);

  return isRequest
    ? ({ type }) => {
      const matches = regExp.exec(type);
      // The capture group only ever yields one of the three phases.
      return matches ? (matches[1] as RequestPhase) : undefined;
    }
    : () => false;
};

const matchCustom = (additionalMatcher: MayBe<MatcherFunction>, action: Action) =>
  (typeof additionalMatcher !== 'function' || additionalMatcher(action));

/**
 * A matcher factory bound to one phase: give it an action root and it tells whether an action is
 * that phase of that root.
 */
export type RequestMatcher = (actionRoot: string, config?: RequestMatcherConfig) => MatcherFunction;

const generateRequestMatcher =
  (type: RequestPhase): RequestMatcher =>
    (actionRoot, { matcher: additionalMatcher, matchRequest = isRequestMatcher } = {}) => {
      const baseMatcher = matchRequest(actionRoot);
      return action => baseMatcher(action) === type && matchCustom(additionalMatcher, action);
    };

export const isStartRequest = generateRequestMatcher('REQUEST');

export const isSuccessRequest = generateRequestMatcher('SUCCESS');

export const isFailureRequest = generateRequestMatcher('FAILURE');

/** An entity identified by a string `id` — the shape the generic reducers fall back on. */
const hasStringId = (entity: unknown): entity is { id: string } =>
  typeof entity === 'object' && entity !== null && 'id' in entity && typeof entity.id === 'string';

/**
 * Reads the `id` of an entity a reducer received, without assuming it is an object: the generic
 * reducers ingest whatever the API returned.
 */
export const readEntityId = (entity: unknown): MayBe<string> => (hasStringId(entity) ? entity.id : undefined);
